import React from 'react';

interface AdminCliHealthCardProps {
  metrics: {
    lastPollAt: string | null;
    isHealthy: boolean;
    minutesSinceLastPoll: number | null;
  } | null;
}

const AdminCliHealthCard: React.FC<AdminCliHealthCardProps> = ({ metrics }) => {
  if (!metrics) {
    return (
      <div className="card p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  const isHealthy = metrics.isHealthy;
  const status: 'healthy' | 'offline' | 'never-connected' =
    metrics.lastPollAt === null ? 'never-connected' : isHealthy ? 'healthy' : 'offline';

  const statusColors = {
    healthy: 'border-green-500 bg-green-50',
    offline: 'border-red-500 bg-red-50',
    'never-connected': 'border-gray-500 bg-gray-50',
  };

  const badgeColors = {
    healthy: 'bg-green-100 text-green-800',
    offline: 'bg-red-100 text-red-800',
    'never-connected': 'bg-gray-100 text-gray-800',
  };

  const statusLabels = {
    healthy: 'ONLINE',
    offline: 'OFFLINE',
    'never-connected': 'NOT STARTED',
  };

  const formatTimestamp = (timestamp: string | null): string => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div className={`card border-l-4 ${statusColors[status]} p-6`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Admin CLI</h3>
          <div className="flex items-center gap-2 mt-2">
            <div className={`w-3 h-3 rounded-full ${status === 'healthy' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
            <p className="text-2xl font-bold text-gray-900">
              {statusLabels[status]}
            </p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badgeColors[status]}`}>
          {status === 'healthy' ? 'ACTIVE' : 'INACTIVE'}
        </span>
      </div>

      {/* Last Poll Info */}
      <div className="space-y-3 mb-4">
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-600">Last Poll:</span>
          <span className="font-medium text-gray-900">
            {formatTimestamp(metrics.lastPollAt)}
          </span>
        </div>
        {metrics.minutesSinceLastPoll !== null && (
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Time Since Poll:</span>
            <span className="font-medium text-gray-900">
              {metrics.minutesSinceLastPoll < 60
                ? `${metrics.minutesSinceLastPoll}m`
                : `${Math.floor(metrics.minutesSinceLastPoll / 60)}h ${metrics.minutesSinceLastPoll % 60}m`}
            </span>
          </div>
        )}
      </div>

      {/* Status Messages */}
      {status === 'offline' && (
        <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded text-xs text-red-800">
          <strong>Critical:</strong> Admin CLI is offline. Manual approvals cannot be processed.
          <div className="mt-1">
            <code className="text-xs">npm start watch</code> in admin-cli directory
          </div>
        </div>
      )}
      {status === 'never-connected' && (
        <div className="mt-3 p-2 bg-gray-100 border border-gray-200 rounded text-xs text-gray-700">
          <strong>Info:</strong> Admin CLI has never connected.
          <div className="mt-1">
            Run <code className="text-xs bg-gray-200 px-1 rounded">npm start watch</code> in admin-cli directory
          </div>
        </div>
      )}
      {status === 'healthy' && (
        <div className="mt-3 p-2 bg-green-100 border border-green-200 rounded text-xs text-green-800">
          <strong>Active:</strong> Admin CLI is polling and ready to process manual approvals.
        </div>
      )}
    </div>
  );
};

export default AdminCliHealthCard;
